/**
 * `useRealtimeStatus()` — high-level NATS (realtime) connection status for UI
 * indicators (e.g. a "Live data" row).
 *
 * Why not just `useGetConnection().state`:
 *   - The FSM defaults OPTIMISTICALLY to `CONNECTED` and only the heartbeat /
 *     online-offline signals (installed once a client exists) move it — so on
 *     mount, before the first connect, the raw state reads CONNECTED. This hook
 *     reports `connecting` until a live socket is actually confirmed (the
 *     `hasEverConnected` latch from the connect-health signal).
 *   - `DEGRADED_NO_REST` (REST down, NATS still up — e.g. the moment airplane
 *     mode fires `navigator offline`) is NOT "live"; this hook collapses every
 *     non-CONNECTED state into `reconnecting`/`offline` so an indicator never
 *     shows green while degraded.
 *
 * States:
 *   - `connecting`   — no live socket established yet this session.
 *   - `connected`    — realtime is live.
 *   - `reconnecting` — degraded (NATS or REST) after having been live; retrying.
 *   - `offline`      — no network.
 */
import * as React from 'react';
import { hasEverConnected, subscribeConnectHealth } from '../signals/connect-health.js';
import { useGetConnection } from './useGetConnection.js';
export function useRealtimeStatus() {
  const everConnected = React.useSyncExternalStore(_temp, _temp2, _temp3);
  const {
    state
  } = useGetConnection();
  if (!everConnected) {
    return "connecting";
  }
  if (state === "CONNECTED") {
    return "connected";
  }
  if (state === "OFFLINE") {
    return "offline";
  }
  return "reconnecting";
}
function _temp3() {
  return false;
}
function _temp2() {
  return hasEverConnected();
}
function _temp(notify) {
  return subscribeConnectHealth(() => notify());
}